const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const BASE_VIEWPORT_WIDTH = 1730;
const RESPONSIVE_BREAKPOINT_DEFAULT = 900;

const setPanelState = (
	panel,
	{
		active,
		position,
		hiddenDirection = 'center',
		stageScale = 1,
		responsiveBreakpoint = RESPONSIVE_BREAKPOINT_DEFAULT,
	}
) => {
			const isMobile = window.matchMedia(`(max-width: ${responsiveBreakpoint}px)`).matches;
	const viewportWidth = window.innerWidth || BASE_VIEWPORT_WIDTH;
	const cappedViewport = Math.min(viewportWidth, BASE_VIEWPORT_WIDTH);
	// Keep baseline composition on ultrawide displays, but let side slides move inward on narrower widths.
	const scaleCompensation = stageScale > 1 ? stageScale : 1;
	const prevDesktopX = ((-48 / 100) * cappedViewport) / scaleCompensation;
	const nextDesktopX = ((45 / 100) * cappedViewport) / scaleCompensation;
	const hiddenLeftDesktopX = ((-90 / 100) * cappedViewport) / scaleCompensation;
	const hiddenRightDesktopX = ((95 / 100) * cappedViewport) / scaleCompensation;
	panel.classList.toggle('is-active', active);
	panel.classList.toggle('is-prev', !active && position === 'prev');
	panel.classList.toggle('is-next', !active && position === 'next');
	panel.classList.toggle('is-hidden', !active && position === 'hidden');
	panel.inert = !active;
	panel.setAttribute('aria-hidden', active ? 'false' : 'true');
	// Opacity and transform retain the depth effect without animating CSS filters,
	// which Lighthouse flags as a non-composited animation.
	panel.style.filter = 'none';

	if (active) {
		panel.style.opacity = '1';
		panel.style.transform = 'translate3d(0, 0, 0) scale(1)';
		panel.style.zIndex = '3';
		return;
	}

	if (position === 'prev') {
		panel.style.opacity = '0.35';
		panel.style.transform = isMobile
			? 'translate3d(-55vw, -50%, -120px) scale(0.3)'
			: `translate3d(${prevDesktopX.toFixed(2)}px, -60%, -160px) scale(0.3)`;
		panel.style.zIndex = '1';
		return;
	}

	if (position === 'next') {
		panel.style.opacity = '0.65';
		panel.style.transform = isMobile
			? 'translate3d(42vw, -55%, -120px) scale(0.3)'
			: `translate3d(${(nextDesktopX + 100).toFixed(2)}px, calc(-75% + 100px), -160px) scale(0.325)`;
		panel.style.zIndex = '1';
		return;
	}

	const hiddenLeftTransform = isMobile
		? 'translate3d(-95vw, -50%, -200px) scale(0.3)'
		: `translate3d(${hiddenLeftDesktopX.toFixed(2)}px, -60%, -220px) scale(0.3)`;
	const hiddenRightTransform = isMobile
		? 'translate3d(95vw, -50%, -200px) scale(0.55)'
		: `translate3d(${hiddenRightDesktopX.toFixed(2)}px, -65%, -220px) scale(0.6)`;
	const hiddenCenterTransform = 'translate3d(0, 120px, 0) scale(1.14)';
	let hiddenTransform = hiddenCenterTransform;
	if (hiddenDirection === 'left') {
		hiddenTransform = hiddenLeftTransform;
	} else if (hiddenDirection === 'right') {
		hiddenTransform = hiddenRightTransform;
	}

	panel.style.opacity = '0';
	panel.style.transform = hiddenTransform;
	panel.style.zIndex = '1';
};

const initPanels = (block) => {
	if (!block || block.dataset.fcServicesScrollInit) {
		return;
	}

	const stageWrap = block.querySelector('.stageWrap');
	const stage = block.querySelector('.stage');
	const stickContent = block.querySelector('.stick-content');
	const panels = Array.from(block.querySelectorAll('.panel'));
	const dots = Array.from(block.querySelectorAll('.dot'));
	const dotsNav = block.querySelector('.dots');
	const skipButton = block.querySelector('.skip');
	const getNextBlock = () => {
		let nextBlock = block.nextElementSibling;
		while (nextBlock && nextBlock.getClientRects().length === 0) {
			nextBlock = nextBlock.nextElementSibling;
		}
		return nextBlock;
	};

	if (!stageWrap || !stage || panels.length === 0) {
		return;
	}

	block.dataset.fcServicesScrollInit = '1';

	let start = 0;
	let end = 0;
	let stickFadeStart = 0;
	let stickFadeEnd = 0;
	let raf = 0;
	let stageScale = 1;
	let panelImagesEnabled = false;
	let lastScrollY = window.scrollY || window.pageYOffset || 0;
	let scrollDirection = 'down';
	let activeIndex = 0;
	const configuredResponsiveBreakpoint = parseInt(
		block.dataset.responsiveBreakpoint || RESPONSIVE_BREAKPOINT_DEFAULT,
		10
	);
	const responsiveBreakpoint = Number.isNaN(configuredResponsiveBreakpoint)
		? RESPONSIVE_BREAKPOINT_DEFAULT
		: configuredResponsiveBreakpoint;

	const loadPanelBackground = (panel) => {
		const mock = panel?.querySelector('.mock[data-background-image]');
		if (!mock) {
			return;
		}

		const useAltImage =
			window.innerWidth <= responsiveBreakpoint && mock.dataset.altBackgroundImage;
		const backgroundImage = useAltImage ? mock.dataset.altBackgroundImage : mock.dataset.backgroundImage;
		if (!backgroundImage || mock.dataset.backgroundLoaded === backgroundImage) {
			return;
		}

		mock.style.backgroundImage = `url("${backgroundImage}")`;
		mock.dataset.backgroundLoaded = backgroundImage;
	};

	const setScrollDirection = (direction) => {
		if (direction === scrollDirection) {
			return;
		}

		scrollDirection = direction;
		if (skipButton) {
			skipButton.classList.toggle('is-up', direction === 'up');
			skipButton.setAttribute(
				'aria-label',
				direction === 'up' ? 'Skip section and scroll up' : 'Skip section and scroll down'
			);
		}
	};

	const measure = () => {
		if (prefersReducedMotion.matches) {
			if (stickContent) {
				stickContent.style.opacity = '1';
				stickContent.style.pointerEvents = 'auto';
			}
			block.style.removeProperty('--fc-services-exit-overlap');
			stageWrap.style.height = 'auto';
			stageWrap.style.paddingTop = '';
			stageWrap.style.paddingBottom = '';
			panels.forEach((panel) => {
				loadPanelBackground(panel);
				panel.inert = false;
				panel.removeAttribute('aria-hidden');
				panel.style.height = '';
				panel.style.opacity = '1';
				panel.style.transform = 'none';
				panel.style.filter = 'none';
				panel.style.position = 'relative';
				panel.style.zIndex = '1';
			});
			return;
		}

		// Measure the following block in its natural position before calculating
		// how much of the unused sticky tail can safely be overlapped.
		block.style.setProperty('--fc-services-exit-overlap', '0px');
		panels.forEach((panel) => {
			panel.style.height = '';
		});
		const maxPanelHeight = panels.reduce((max, panel) => {
			const height = panel.scrollHeight || panel.offsetHeight || 0;
			return Math.max(max, height);
		}, 0);
		if (maxPanelHeight > 0) {
			block.style.setProperty('--fc-panel-height', `${maxPanelHeight}px`);
			panels.forEach((panel) => {
				panel.style.height = `${maxPanelHeight}px`;
			});
		}
		if (stickContent) {
			const stickHeight = stickContent.offsetHeight || 0;
			block.style.setProperty('--fc-stick-half-height', `${stickHeight * 0.5}px`);
		}

		const stageHeight = stage.offsetHeight || 0;
		const totalPanels = panels.length;
		const viewportHeight = window.innerHeight || stageHeight;
		const viewportWidth = window.innerWidth || 1750;
		const centeredOffset = Math.max(0, (viewportHeight - stageHeight) / 2);
		// Reduce overall spacer so content below isn't pushed too far down.
		const isMobile = window.matchMedia(`(max-width: ${responsiveBreakpoint}px)`).matches;
		stage.style.touchAction = isMobile ? 'pan-y' : '';
		const baseDistance = stageHeight * Math.max(1, totalPanels - 1) * 2.0 + (isMobile ? stageHeight * 0.2 : 100);
		// Keep a subtle upscale on wider screens without making panels feel noticeably taller.
		const scale = isMobile
			? clamp(viewportWidth / 400, 0.9, 1.0)
			: clamp(viewportWidth / 1300, 0.95, 1.05);
		stageScale = scale;
		block.style.setProperty('--fc-services-scale', scale.toFixed(3));
		const trimFactor =
			viewportHeight > 1150 ? 0.62 : viewportHeight > 700 ? 0.52 : 0.4;
		const trimmedDistance = Math.max(stageHeight, baseDistance - stageHeight * trimFactor);
		const scaledDistance = trimmedDistance * scale;
		const scaledStageHeight = stageHeight * scale;
		const scrollRange = Math.max(0, scaledDistance - scaledStageHeight);
		// The final panel becomes active before the sticky range ends. Only overlap
		// that unused tail when the next block can still clear the rendered controls.
		const lastPanelStart =
			totalPanels > 1 ? (totalPanels - 1.5) / (totalPanels - 1) : 0;
		const finalPanelTail = scrollRange * (1 - clamp(lastPanelStart, 0, 1));
		stageWrap.style.height = `${scaledDistance}px`;
		stageWrap.style.marginBottom = '';
		stageWrap.style.paddingTop = '0px';
		stageWrap.style.paddingBottom = '0px';
		const viewHeight = window.innerHeight || stageHeight;
		const topOffset = clamp((viewHeight - 600) / 1200, 0, 1);
		const topPercent = 10 + topOffset * 10 + (isMobile ? 8 : 0);
		stage.style.top = `${topPercent}%`;
		stage.style.transform = `translateY(-${topPercent}%) scale(${scale})`;

		const wrapRect = stageWrap.getBoundingClientRect();
		const scrollY = window.scrollY || window.pageYOffset || 0;
		start = wrapRect.top + scrollY;
		end = start + scaledDistance - scaledStageHeight;
		const stageRect = stage.getBoundingClientRect();
		const panelBottomWithinStage =
			((stageHeight + maxPanelHeight) * scale) / 2;
		const dotsBottomWithinStage = dotsNav
			? dotsNav.getBoundingClientRect().bottom - stageRect.top
			: 0;
		const finalContentBottomWithinStage = Math.max(
			panelBottomWithinStage,
			dotsBottomWithinStage
		);
		const stageVisualOffset =
			-(stageHeight * topPercent) / 100 + (stageHeight * (1 - scale)) / 2;
		const finalStageTop =
			start + scaledDistance - stageHeight + stageVisualOffset;
		const finalContentBottom = finalStageTop + finalContentBottomWithinStage;
		const nextBlock = getNextBlock();
		const nextContent =
			nextBlock?.querySelector('h1, h2, h3, h4, h5, h6, p') || nextBlock;
		const nextContentTop = nextContent
			? nextContent.getBoundingClientRect().top + scrollY
			: finalContentBottom;
		const desiredGap = 100;
		const safeOverlap = Math.max(
			0,
			nextContentTop - finalContentBottom - desiredGap
		);
		const exitOverlap = Math.min(finalPanelTail, safeOverlap);
		block.style.setProperty(
			'--fc-services-exit-overlap',
			`-${exitOverlap.toFixed(2)}px`
		);
		stickFadeStart = Math.max(0, start - viewportHeight * 0.6);
		stickFadeEnd = stickFadeStart + Math.max(180, viewportHeight * 0.22);
		update();
	};

	const renderPanelStates = (index) => {
		if (panelImagesEnabled) {
			[index - 1, index, index + 1].forEach((panelIndex) => {
				if (panels[panelIndex]) {
					loadPanelBackground(panels[panelIndex]);
				}
			});
		}

		panels.forEach((panel, idx) => {
			let position = 'hidden';
			let hiddenDirection = 'center';
			if (idx === index - 1) {
				position = 'prev';
			} else if (idx === index + 1) {
				position = 'next';
			} else if (idx < index) {
				hiddenDirection = 'left';
			} else if (idx > index) {
				hiddenDirection = 'right';
			}
			setPanelState(panel, {
				active: idx === index,
				position,
				hiddenDirection,
				stageScale,
				responsiveBreakpoint,
			});
		});

		dots.forEach((dot, idx) => {
			dot.setAttribute('aria-current', idx === index ? 'true' : 'false');
		});
	};

	const update = () => {
		if (prefersReducedMotion.matches) {
			return;
		}
		const scrollY = window.scrollY || window.pageYOffset || 0;
		if (stickContent) {
			const fadeProgress =
				stickFadeEnd > stickFadeStart
					? clamp((scrollY - stickFadeStart) / (stickFadeEnd - stickFadeStart), 0, 1)
					: 0;
			stickContent.style.opacity = `${(1 - fadeProgress).toFixed(3)}`;
			stickContent.style.pointerEvents = fadeProgress > 0.95 ? 'none' : 'auto';
		}

		const progress = end > start ? (scrollY - start) / (end - start) : 0;
		const clamped = clamp(progress, 0, 1);
		const rawIndex = clamped * (panels.length - 1);
		activeIndex = Math.round(rawIndex);
		renderPanelStates(activeIndex);
	};

	const onScroll = () => {
		const scrollY = window.scrollY || window.pageYOffset || 0;
		if (scrollY > lastScrollY) {
			setScrollDirection('down');
		} else if (scrollY < lastScrollY) {
			setScrollDirection('up');
		}
		lastScrollY = scrollY;

		if (raf) {
			return;
		}
		raf = window.requestAnimationFrame(() => {
			raf = 0;
			update();
		});
	};

	const scrollToIndex = (index, behavior = 'smooth') => {
		const total = panels.length - 1;
		const safeIndex = clamp(index, 0, total);
		const targetProgress = total > 0 ? safeIndex / total : 0;
		const target = start + targetProgress * (end - start);
		window.scrollTo({ top: target, behavior });
	};

	let swipe = null;
	let swipeSettleTimer = 0;
	let suppressClickUntil = 0;
	const finishSwipe = (index = activeIndex) => {
		if (swipeSettleTimer) {
			window.clearTimeout(swipeSettleTimer);
		}
		panels.forEach((panel) => {
			panel.style.transition = 'opacity 350ms ease-out, transform 350ms ease-out';
			panel.style.willChange = 'opacity, transform';
		});
		swipe = null;
		stage.classList.remove('is-swiping');
		renderPanelStates(index);
		swipeSettleTimer = window.setTimeout(() => {
			panels.forEach((panel) => {
				panel.style.removeProperty('transition');
				panel.style.removeProperty('will-change');
			});
			swipeSettleTimer = 0;
		}, 380);
	};
	const isMobileViewport = () =>
		window.matchMedia(`(max-width: ${responsiveBreakpoint}px)`).matches;
	const renderSwipe = (deltaX) => {
		const direction = deltaX < 0 ? 1 : -1;
		const targetIndex = activeIndex + direction;
		const currentPanel = panels[activeIndex];
		const targetPanel = panels[targetIndex];
		const swipeDistance = Math.max(160, Math.min(stage.clientWidth * 0.7, 420));

		panels.forEach((panel) => {
			panel.style.transition = 'none';
			panel.style.willChange = 'opacity, transform';
		});
		renderPanelStates(activeIndex);

		if (!targetPanel) {
			const resistedX = deltaX * 0.16;
			currentPanel.style.transform = `translate3d(${resistedX.toFixed(2)}px, 0, 0) scale(1)`;
			return;
		}

		const progress = clamp(Math.abs(deltaX) / swipeDistance, 0, 1);
		const incomingX = direction > 0 ? 42 : -55;
		const incomingY = direction > 0 ? -55 : -50;
		const incomingOpacity = direction > 0 ? 0.65 : 0.35;
		const outgoingX = direction > 0 ? -55 : 42;
		const outgoingY = direction > 0 ? -50 : -55;
		const outgoingOpacity = direction > 0 ? 0.35 : 0.65;

		currentPanel.style.opacity = `${1 - (1 - outgoingOpacity) * progress}`;
		currentPanel.style.transform = `translate3d(${outgoingX * progress}vw, ${outgoingY * progress}%, ${-120 * progress}px) scale(${1 - 0.7 * progress})`;
		targetPanel.style.opacity = `${incomingOpacity + (1 - incomingOpacity) * progress}`;
		targetPanel.style.transform = `translate3d(${incomingX * (1 - progress)}vw, ${incomingY * (1 - progress)}%, ${-120 * (1 - progress)}px) scale(${0.3 + 0.7 * progress})`;
	};

	stage.addEventListener('pointerdown', (event) => {
		if (
			event.pointerType === 'mouse' ||
			!isMobileViewport() ||
			prefersReducedMotion.matches ||
			panels.length < 2
		) {
			return;
		}
		if (swipeSettleTimer) {
			window.clearTimeout(swipeSettleTimer);
			swipeSettleTimer = 0;
		}

		swipe = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			startedAt: performance.now(),
			axis: null,
		};
	});

	stage.addEventListener('pointermove', (event) => {
		if (!swipe || event.pointerId !== swipe.pointerId) {
			return;
		}

		const deltaX = event.clientX - swipe.startX;
		const deltaY = event.clientY - swipe.startY;
		if (!swipe.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 10) {
			swipe.axis = Math.abs(deltaX) > Math.abs(deltaY) * 1.15 ? 'x' : 'y';
			if (swipe.axis === 'x') {
				stage.classList.add('is-swiping');
			}
		}

		if (swipe.axis === 'x') {
			event.preventDefault();
			renderSwipe(deltaX);
		}
	});

	stage.addEventListener('pointerup', (event) => {
		if (!swipe || event.pointerId !== swipe.pointerId) {
			return;
		}

		const deltaX = event.clientX - swipe.startX;
		const elapsed = Math.max(performance.now() - swipe.startedAt, 1);
		const isHorizontalSwipe =
			swipe.axis === 'x' &&
			(Math.abs(deltaX) >= 48 ||
				(Math.abs(deltaX) >= 24 && Math.abs(deltaX) / elapsed > 0.35));

		if (swipe.axis === 'x') {
			event.preventDefault();
			suppressClickUntil = performance.now() + 400;
		}

		if (isHorizontalSwipe) {
			const targetIndex = clamp(activeIndex + (deltaX < 0 ? 1 : -1), 0, panels.length - 1);
			setScrollDirection(deltaX < 0 ? 'down' : 'up');
			activeIndex = targetIndex;
			finishSwipe(targetIndex);
			scrollToIndex(targetIndex, 'auto');
			return;
		}

		finishSwipe();
	});

	stage.addEventListener('pointercancel', () => finishSwipe());
	stage.addEventListener(
		'click',
		(event) => {
			if (performance.now() < suppressClickUntil) {
				event.preventDefault();
				event.stopPropagation();
			}
		},
		true
	);

	const scrollToNextBlock = () => {
		const nextBlock = getNextBlock();

		if (!nextBlock) {
			window.scrollTo({ top: end + 40, behavior: 'smooth' });
			return;
		}

		const scrollY = window.scrollY || window.pageYOffset || 0;
		const nextBlockTop = nextBlock.getBoundingClientRect().top + scrollY;
		window.scrollTo({ top: Math.max(0, nextBlockTop), behavior: 'smooth' });
	};

	const scrollToBlockStart = () => {
		const scrollY = window.scrollY || window.pageYOffset || 0;
		const blockTop = block.getBoundingClientRect().top + scrollY;
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
		const centeredBlockTop = blockTop - viewportHeight / 2;
		window.scrollTo({ top: Math.max(0, centeredBlockTop), behavior: 'smooth' });
	};

	dots.forEach((dot) => {
		dot.addEventListener('click', () => {
			const target = parseInt(dot.dataset.goto || '0', 10);
			scrollToIndex(Number.isNaN(target) ? 0 : target);
		});
	});

	if (skipButton) {
		skipButton.addEventListener('click', () => {
			if (scrollDirection === 'up') {
				scrollToBlockStart();
				return;
			}

			scrollToNextBlock();
		});
	}

	if ('IntersectionObserver' in window) {
		const imageObserver = new IntersectionObserver((entries) => {
			if (!entries.some((entry) => entry.isIntersecting)) {
				return;
			}

			panelImagesEnabled = true;
			imageObserver.disconnect();
			update();
		}, { rootMargin: '30% 0px' });
		imageObserver.observe(block);
	} else {
		panelImagesEnabled = true;
	}

	measure();
	window.addEventListener('resize', measure);
	window.addEventListener('scroll', onScroll, { passive: true });
	prefersReducedMotion.addEventListener('change', measure);
};

const initAll = () => {
	// Guard against running frontend scroll animation calculations and window listeners inside the CMS block editor
	if (
		(document.body && document.body.classList.contains('wp-admin')) ||
		typeof window.wp?.blockEditor !== 'undefined' ||
		document.querySelector('.editor-styles-wrapper')
	) {
		return;
	}
	document.querySelectorAll('.wp-block-fc-block-fc-services-scroll').forEach((block) => {
		initPanels(block);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAll);
} else {
	initAll();
}
